import { useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../hooks/useConfirmation";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import ShoppingListItemForm from "./ShoppingListItemForm";
import ShoppingListItemRow from "./ShoppingListItemRow";

const ShoppingListItem = ({ item, index, onUpdateItem, onDeleteItem, hidePurchased }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isEditing, setIsEditing] = useState(false);
  const itemRef = useRef(null);
  const { confirm, dialogProps } = useConfirmation();

  const handleToggleEdit = useCallback(() => {
    if (!isEditing) {
      setIsEditing(true);
    }
  }, [isEditing]);

  const handleSave = useCallback(
    (updatedItem) => {
      onUpdateItem(index, updatedItem);
      setIsEditing(false);
    },
    [index, onUpdateItem],
  );

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: t("shoppingList.deleteItem"),
      message: t("shoppingList.confirmDelete"),
      confirmText: t("shoppingList.delete"),
      confirmButtonColor: "error",
    });

    if (confirmed) {
      setIsEditing(false);
      onDeleteItem(index);
    }
  }, [confirm, index, onDeleteItem, t]);

  // Close edit mode when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isEditing && itemRef.current && !itemRef.current.contains(event.target)) {
        handleCancel();
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, handleCancel]);

  return (
    <div ref={itemRef}>
      {isEditing ? (
        <ShoppingListItemForm item={item} theme={theme} onSave={handleSave} onCancel={handleCancel} onDelete={handleDelete} />
      ) : (
        <ShoppingListItemRow
          item={item}
          theme={theme}
          onItemClick={handleToggleEdit}
          onDeleteClick={handleDelete}
          onCheckedChange={(checkedValue) => handleSave({ ...item, purchased: checkedValue })}
          hidePurchased={hidePurchased}
        />
      )}

      <ConfirmationDialog {...dialogProps} />
    </div>
  );
};

export default ShoppingListItem;
